import { SearchOutlined } from '@ant-design/icons';
import {
  Input as AntInput,
  type InputProps,
  type InputRef,
  type TableProps
} from 'antd';
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  type ChangeEvent,
  type Key,
  type ReactElement,
  type ReactNode,
  type Ref
} from 'react';

import type { RouteQueryActions, RouteQueryState } from '@/hooks';
import type { TrackEventFunction } from '@/hooks/useTrackEvents/types';
import { noop } from '@/utils/noop';
import type { Sort } from '@/utils/types';

import FilterButton from '../FilterButton';
import type { Filters, Input } from '../FilterButton/types';
import withDebouncedOnChange from '../withDebouncedOnChange';

import {
  BulkOperationFooter,
  ColumnReorderProvider,
  ColumnVisibilityButton,
  RowSelectionMenuButton,
  SortButton,
  TablePagination,
  UnsavedChangesBar
} from './components';
import type { SortMenuOption } from './components/SortButton';
import {
  DEFAULT_INPUT_SEARCH_DEBOUNCE_MS,
  EXPANDABLE_CONFIG,
  SCROLL_SETTINGS,
  VIRTUAL_SELECTION_COLUMN_WIDTH,
  VIRTUAL_SELECTION_COLUMN_WIDTH_BORDERED
} from './constants';
import {
  useBulkOperations,
  useFilterTags,
  useSortableColumns,
  useTableHighlights,
  useVirtualScroll
} from './hooks';
import * as Styled from './styles';
import type {
  BulkOperationsConfig,
  BulkOperationType,
  CellHighlight,
  RowHighlight,
  SearchConfig,
  TableViewRef,
  TabsConfig,
  UnsavedChangesConfig
} from './types';
import {
  getRowTestId,
  getVisibleColumnKeys,
  getVisibleColumnsWidth
} from './utils';

const DebouncedSearchInput = withDebouncedOnChange<InputRef, InputProps>(
  AntInput
);

export type Props<T extends object> = Omit<TableProps<T>, 'title'> & {
  mode?: 'normal' | 'section-less';
  tableId?: string;
  leftMenu?: ReactNode;
  rightMenu?: ReactNode;
  topSide?: ReactNode;
  rightSide?: ReactNode;
  leftSide?: ReactNode;
  filterConfig?: Record<string, Input>;
  searchConfig?: SearchConfig;
  /**
   * Replaces the search box with the page's own control, e.g. one applied with
   * a button. The filter tags move to the row below, as with `searchConfig`.
   */
  customSearch?: ReactNode;
  hideFilterButton?: boolean;
  selectedBulkOperationType?: BulkOperationType;
  sort?: Sort;
  onSort?: (value: Sort | undefined) => void;
  showColumnVisibilityButton?: boolean;
  showBulkOperationsButton?: boolean;
  rowClassName?: (record: T, index: number) => string;
  rowHighlights?: RowHighlight[];
  cellHighlights?: CellHighlight[];
  bulkOperationConfig?: BulkOperationsConfig;
  unsavedChangesConfig?: UnsavedChangesConfig;
  hideHeaderWhenEmpty?: boolean;
  tabsConfig?: TabsConfig;
  trackEvent?: TrackEventFunction;
  /**
   * The table's query state and the actions that mutate it. The owning page
   * holds a single `useRouteQueryState({ tableId })` and passes both down — the
   * table never creates query state itself. Omit both only for a static table
   * with no filters, pagination, sort, or column-visibility/reorder features.
   */
  queryState?: RouteQueryState;
  queryActions?: RouteQueryActions;
  reorderableColumns?: boolean;
  virtual?: boolean;
  scrollY?: number;
  /** Overrides the page-size choices offered by the pagination size changer. */
  pageSizeOptions?: number[];
  /**
   * Sortable columns for the "Sort by" button, rendered after the
   * column-visibility button. Uses the same `sort`/`onSort` (or
   * `queryActions.setSort`) as column-header sorting — pick one mechanism per
   * table, not both.
   */
  sortOptions?: SortMenuOption[];
};

const TableView = <T extends object>(
  {
    loading,
    dataSource,
    columns,
    leftMenu,
    rightMenu,
    topSide,
    sort,
    rowKey,
    filterConfig,
    searchConfig,
    customSearch,
    hideFilterButton = false,
    rowSelection,
    locale,
    bordered = true,
    pagination = false,
    hideHeaderWhenEmpty = false,
    rightSide,
    leftSide,
    onSort,
    tabsConfig,
    showColumnVisibilityButton = true,
    showBulkOperationsButton = true,
    bulkOperationConfig,
    mode = 'normal',
    selectedBulkOperationType,
    rowClassName,
    rowHighlights,
    cellHighlights,
    unsavedChangesConfig,
    trackEvent = noop,
    queryState,
    queryActions,
    reorderableColumns,
    virtual = false,
    scrollY,
    onRow,
    pageSizeOptions,
    sortOptions,
    ...tableProps
  }: Props<T>,
  ref: Ref<TableViewRef>
) => {
  const {
    filters,
    pagination: paginationParams,
    hiddenColumns,
    columnOrder
  } = queryState ?? {};

  const hasUnsavedChanges = (unsavedChangesConfig?.count ?? 0) > 0;

  const handleFilter = useCallback(
    (next?: Filters) => {
      if (!queryActions) return;
      if (next) queryActions.setFilters(next);
      else queryActions.clearFilters();
    },
    [queryActions]
  );

  const { enhancedColumns } = useSortableColumns({
    columns,
    sort,
    onSort,
    setSort: queryActions?.setSort ?? noop,
    hiddenColumns,
    trackEvent
  });

  const { getRowHighlightClassName, getCellHighlightedColumns } =
    useTableHighlights({
      rowHighlights,
      cellHighlights,
      rowKey:
        typeof rowKey === 'function'
          ? (record, index) => rowKey(record as T, index)
          : rowKey
    });

  // Closing a tag is a filter change like any other, so it reports the same way
  // the Filter button and the column-header filters do.
  const handleRemoveFilterTag = useCallback(
    (name: string) => {
      trackEvent('Click to remove filter tag', { filter: name });
      queryActions?.removeFilter(name);
    },
    [queryActions, trackEvent]
  );

  const { filterTagList } = useFilterTags({
    filterConfig,
    params: filters,
    removeParam: handleRemoveFilterTag
  });

  const hasSearch = Boolean(customSearch || searchConfig);

  const orderedColumnsRef = useRef<Array<{ key?: Key }>>([]);

  const {
    selectedBulkOperation,
    rowSelectionConfig,
    shouldRenderBulkDropDown,
    handleCancelAndClear
  } = useBulkOperations({
    rowSelection,
    dataSource,
    bulkOperationConfig,
    selectedBulkOperationType,
    showBulkOperationsButton
  });

  useImperativeHandle(
    ref,
    () => ({
      onCancelAndClear: handleCancelAndClear,
      getVisibleColumns: () =>
        getVisibleColumnKeys(orderedColumnsRef.current, hiddenColumns)
    }),
    [handleCancelAndClear, hiddenColumns]
  );

  const tabItems = useMemo(() => {
    if (!tabsConfig?.tabs) return undefined;
    return tabsConfig.tabs.map(tab => ({
      key: tab.key,
      label: tab.label
    }));
  }, [tabsConfig?.tabs]);

  const expandable = useMemo(
    () => tableProps?.expandable ?? EXPANDABLE_CONFIG,
    [tableProps?.expandable]
  );

  // Compose shared row highlighting with any caller-provided row class.
  const getRowClassName = useCallback(
    (record: T, index: number) =>
      [rowClassName?.(record, index), getRowHighlightClassName(record, index)]
        .filter(Boolean)
        .join(' '),
    [rowClassName, getRowHighlightClassName]
  );

  // Resolve a row's identity the same way AntD does: an explicit rowKey
  // function/field, else `record.key` (rc-table's own default is the string
  // 'key', not the row index — see rc-table's `getRowKey`), else the index as
  // a last resort so the test-id hook always has something to render.
  const resolveRowKey = useCallback(
    (record: T, index: number): Key => {
      if (typeof rowKey === 'function') return rowKey(record, index);
      if (typeof rowKey === 'string') {
        const value = (record as Record<string, unknown>)[rowKey];
        if (value != null) return value as Key;
      } else {
        const value = (record as Record<string, unknown>).key;
        if (value != null) return value as Key;
      }
      return index;
    },
    [rowKey]
  );

  // Tag every row with a stable `data-testid` built from its key so E2E specs
  // can anchor on a row without filtering by its text content, while preserving
  // whatever the consumer's own `onRow` returns (e.g. row-click handlers). The
  // spread suppresses excess-property checks so `data-testid` rides alongside
  // the typed HTMLAttributes.
  const getRowProps = useCallback<NonNullable<TableProps<T>['onRow']>>(
    (record, index) => ({
      ...onRow?.(record, index),
      'data-testid': getRowTestId(resolveRowKey(record, index ?? 0))
    }),
    [onRow, resolveRowKey]
  );

  const { tableContainerRef, virtualScroll } = useVirtualScroll({
    virtual,
    scrollY
  });

  // AntD's virtual body grid sizes cells from `column.width`/`column.minWidth`
  // and ignores the CSS-token width that the non-virtual selection column
  // relies on. Without an explicit width the column collapses to 0px and the
  // checkboxes get clipped by the next column. Inject a default that mirrors
  // the non-virtual visuals; consumer-provided `columnWidth` still wins.
  const rowSelectionForTable = useMemo(() => {
    if (!virtual || !rowSelectionConfig) return rowSelectionConfig;
    if (rowSelectionConfig.columnWidth !== undefined) return rowSelectionConfig;
    return {
      ...rowSelectionConfig,
      columnWidth: bordered
        ? VIRTUAL_SELECTION_COLUMN_WIDTH_BORDERED
        : VIRTUAL_SELECTION_COLUMN_WIDTH
    };
  }, [virtual, rowSelectionConfig, bordered]);

  return (
    <ColumnReorderProvider
      enabled={Boolean(reorderableColumns)}
      columns={enhancedColumns}
      columnOrder={columnOrder}
      onColumnOrderChange={queryActions?.setColumnOrder ?? noop}>
      {({ orderedColumns, headerComponents, wrap }) => {
        orderedColumnsRef.current = orderedColumns ?? [];

        // Derive the virtual table's horizontal scroll width from the columns
        // that are actually visible. AntD's virtual body requires a numeric
        // `scroll.x` and uses it as the lower bound for the grid width
        // (`max(scroll.x, containerWidth)`); pinning it to the real
        // visible-column total lets the table behave like the non-virtual one:
        // columns share the container width when they fit, and scroll only
        // once their combined width exceeds it. A hardcoded value instead
        // over-scaled every column (fixed included). See getVisibleColumnsWidth.
        const selectionColumnWidth =
          typeof rowSelectionForTable?.columnWidth === 'number'
            ? rowSelectionForTable.columnWidth
            : 0;
        const virtualScrollX = virtual
          ? getVisibleColumnsWidth(orderedColumns, selectionColumnWidth)
          : undefined;
        const virtualScrollConfig =
          virtualScroll && virtualScrollX
            ? { ...virtualScroll, x: virtualScrollX }
            : virtualScroll;

        const sharedTableProps = {
          loading,
          dataSource,
          columns: getCellHighlightedColumns(orderedColumns),
          expandable,
          locale,
          rowKey,
          bordered,
          pagination: false as const,
          rowSelection: rowSelectionForTable,
          rowClassName: getRowClassName,
          onRow: getRowProps,
          showHeader: !(hideHeaderWhenEmpty && !dataSource?.length),
          virtual,
          ...tableProps,
          components: {
            ...tableProps?.components,
            ...headerComponents,
            header: {
              ...tableProps?.components?.header,
              ...headerComponents?.header
            }
          }
        };

        const renderTable = (extraProps: Partial<TableProps<T>>) =>
          wrap(<Styled.Table {...sharedTableProps} {...extraProps} />);

        if (mode === 'section-less') {
          return (
            <>
              {renderTable(
                virtual
                  ? { sticky: false, scroll: virtualScrollConfig }
                  : { sticky: true }
              )}
            </>
          );
        }

        return (
          <Styled.Root>
            <Styled.Header>
              {hasSearch ? (
                <Styled.SearchContainer $maxWidth={searchConfig?.maxWidth}>
                  {customSearch ??
                    (searchConfig && (
                      <DebouncedSearchInput
                        // Remounting is what re-seeds the input from `value`:
                        // it keeps the typed text internally and only follows
                        // `value` when that changes, which a declined search
                        // does not.
                        key={searchConfig.resetToken}
                        // That reset unmounts the input, so flushing on
                        // unmount would re-apply the very search the user just
                        // declined.
                        flushOnUnmount={false}
                        allowClear
                        suffix={<SearchOutlined />}
                        placeholder={searchConfig.placeholder ?? 'Search'}
                        value={searchConfig.value}
                        debounceMs={
                          searchConfig.debounceMs ??
                          DEFAULT_INPUT_SEARCH_DEBOUNCE_MS
                        }
                        flushOnEmptyValue={searchConfig.flushOnClear}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          searchConfig.onChange(e.target.value)
                        }
                      />
                    ))}
                </Styled.SearchContainer>
              ) : (
                <Styled.LeftMenuContainer>
                  <Styled.LeftMenuContent>
                    {leftMenu}
                    {filterTagList}
                  </Styled.LeftMenuContent>
                </Styled.LeftMenuContainer>
              )}
              <Styled.HeaderActions gap={10}>
                {shouldRenderBulkDropDown && (
                  <RowSelectionMenuButton
                    bulkOperationOptions={bulkOperationConfig!.options}
                  />
                )}
                {rightMenu}
                {filterConfig && !hideFilterButton && queryActions && (
                  <FilterButton
                    trackEvent={trackEvent}
                    filters={filters}
                    config={filterConfig}
                    onFilter={handleFilter}
                  />
                )}
                {showColumnVisibilityButton && queryActions && (
                  <ColumnVisibilityButton
                    columns={orderedColumns}
                    onHide={queryActions.setHiddenColumns}
                  />
                )}
                {!!sortOptions?.length && (
                  <SortButton
                    options={sortOptions}
                    sort={sort}
                    onSort={onSort ?? queryActions?.setSort ?? noop}
                    trackEvent={trackEvent}
                  />
                )}
              </Styled.HeaderActions>
            </Styled.Header>

            {hasSearch && (
              <Styled.FilterTagsRow>
                {leftMenu}
                {filterTagList}
              </Styled.FilterTagsRow>
            )}

            {tabItems && (
              <Styled.TabsWrapper
                items={tabItems}
                activeKey={tabsConfig?.activeKey}
                defaultActiveKey={tabsConfig?.defaultActiveKey}
                onChange={tabsConfig?.onChange}
              />
            )}
            <Styled.TableContainer ref={tableContainerRef}>
              <Styled.TableContent>
                {leftSide}
                <Styled.OuterScroll $virtual={virtual}>
                  {topSide}
                  {renderTable({
                    scroll: virtual ? virtualScrollConfig : SCROLL_SETTINGS,
                    sticky: !virtual && !!dataSource?.length
                  })}
                </Styled.OuterScroll>
                {rightSide}
              </Styled.TableContent>
            </Styled.TableContainer>

            {(pagination || selectedBulkOperation || hasUnsavedChanges) && (
              <Styled.Footer>
                <div>
                  {pagination && queryActions && (
                    <TablePagination
                      pagination={pagination}
                      paginationParams={paginationParams}
                      rightSide={rightSide}
                      setPagination={queryActions.setPagination}
                      pageSizeOptions={pageSizeOptions}
                    />
                  )}
                </div>
                {selectedBulkOperation && (
                  <BulkOperationFooter
                    selectedKeys={rowSelection?.selectedRowKeys || []}
                    selectedBulkOperation={selectedBulkOperation}
                    onCancelAndClear={handleCancelAndClear}
                  />
                )}
                {/* Bulk selection takes precedence: the unsaved-changes bar is
                    hidden while a bulk operation is active so the two footers
                    never render together. */}
                {!selectedBulkOperation &&
                  hasUnsavedChanges &&
                  unsavedChangesConfig && (
                    <UnsavedChangesBar
                      count={unsavedChangesConfig.count}
                      itemNoun={unsavedChangesConfig.itemNoun}
                      isSaving={unsavedChangesConfig.isSaving}
                      disabled={unsavedChangesConfig.disabled}
                      onCancel={unsavedChangesConfig.onCancel}
                      onSave={unsavedChangesConfig.onSave}
                    />
                  )}
              </Styled.Footer>
            )}
          </Styled.Root>
        );
      }}
    </ColumnReorderProvider>
  );
};

export default forwardRef(TableView) as <T extends object>(
  props: Props<T> & { ref?: Ref<TableViewRef> }
) => ReactElement;
